// BriefBuilder engine — pure logic.

export interface Brief {
  id: string;
  projectName: string;
  clientName: string;
  date: string;
  callType: 'discovery' | 'requirements' | 'kickoff';
  goal: string;
  problem: string;
  audience: string;
  successMetrics: string[];
  scopeIn: string[];
  scopeOut: string[];
  constraints: string[];
  assumptions: string[];
  risks: string[];
  decisions: string[];
  nextSteps: { what: string; owner: string; due: string }[];
}

export const QUESTION_TEMPLATES: Record<string, string[]> = {
  discovery: [
    'What problem are you trying to solve?',
    'Who is the user? Be specific.',
    'How is this being done today? What\'s the workaround?',
    'What does success look like in 6 months?',
    'What is the budget range?',
    'What is the timeline driver?',
    'What is explicitly out of scope?',
    'Who owns the decision on go/no-go?',
    'What systems must we integrate with?',
    'What compliance / regulatory constraints apply?',
  ],
  requirements: [
    'Walk me through the happy path.',
    'What are the edge cases that worry you?',
    'What data goes in? What comes out?',
    'Where does this live in the broader workflow?',
    'Who else needs to approve the deliverable?',
    'What level of automation is required?',
    'What are the non-negotiable performance targets?',
  ],
  kickoff: [
    'Who is the day-to-day point of contact?',
    'What is the weekly cadence?',
    'How do we handle scope changes?',
    'What is the escalation path?',
    'What dashboards / status do you need?',
    'What does "done" look like for each milestone?',
  ],
};

export function emptyBrief(): Brief {
  return {
    id: crypto.randomUUID(),
    projectName: '',
    clientName: '',
    date: new Date().toISOString().slice(0, 10),
    callType: 'discovery',
    goal: '',
    problem: '',
    audience: '',
    successMetrics: [],
    scopeIn: [],
    scopeOut: [],
    constraints: [],
    assumptions: [],
    risks: [],
    decisions: [],
    nextSteps: [],
  };
}

export function briefCompleteness(b: Brief): number {
  let score = 0;
  if (b.goal.trim()) score += 15;
  if (b.problem.trim()) score += 10;
  if (b.audience.trim()) score += 10;
  if (b.successMetrics.length > 0) score += 15;
  if (b.scopeIn.length > 0) score += 10;
  if (b.scopeOut.length > 0) score += 10;
  if (b.constraints.length > 0) score += 5;
  if (b.assumptions.length > 0) score += 5;
  if (b.risks.length > 0) score += 10;
  if (b.nextSteps.length > 0) score += 10;
  return score;
}

export function exportBriefMarkdown(b: Brief): string {
  const list = (arr: string[]) => arr.length ? arr.map(x => `- ${x}`).join('\n') : '_(none)_';
  return `# ${b.projectName || 'Project Brief'}

**Client:** ${b.clientName}
**Date:** ${b.date}
**Call type:** ${b.callType}

## Goal
${b.goal || '_(not captured)_'}

## Problem
${b.problem || '_(not captured)_'}

## Audience
${b.audience || '_(not captured)_'}

## Success metrics
${list(b.successMetrics)}

## In scope
${list(b.scopeIn)}

## Out of scope
${list(b.scopeOut)}

## Constraints
${list(b.constraints)}

## Assumptions
${list(b.assumptions)}

## Risks
${list(b.risks)}

## Decisions made
${list(b.decisions)}

## Next steps
${b.nextSteps.length ? b.nextSteps.map(n => `- **${n.what}** — ${n.owner} (due ${n.due})`).join('\n') : '_(none)_'}

---
_Generated by CatalystAgile BriefBuilder · Completeness: ${briefCompleteness(b)}%_
`;
}
